import { CommonModule } from '@angular/common';
import { Component, OnDestroy, OnInit } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { Router } from '@angular/router';
import { Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';

import { PageBreadcrumbComponent } from '../../../../../shared/components/common/page-breadcrumb/page-breadcrumb.component';
import { ConfirmDialogService } from '../../../../../shared/services/confirm-dialog.service';
import { ToastService } from '../../../../../shared/services/toast.service';
import { HasPermissionDirective } from '../../../../../shared/directives/has-permission.directive';
import { TicketsConfigApiService } from '../../../services/tickets-config-api.service';
import { TicketsPriorityDrawerService } from '../../../services/tickets-config-drawer.services';
import { TicketPriority } from '../../../models/tickets-config.model';

@Component({
  selector: 'app-tickets-priorities-list',
  standalone: true,
  imports: [CommonModule, FormsModule, PageBreadcrumbComponent, HasPermissionDirective],
  templateUrl: './priorities-list.component.html'
})
export class TicketsPrioritiesListComponent implements OnInit, OnDestroy {
  items: TicketPriority[] = [];
  filtered: TicketPriority[] = [];
  loading = false;
  error = '';

  filtroNombre = '';
  filtroSoloActivas = false;

  private readonly destroy$ = new Subject<void>();

  constructor(
    protected router: Router,
    private configApi: TicketsConfigApiService,
    private drawer: TicketsPriorityDrawerService,
    private confirmDialog: ConfirmDialogService,
    private toast: ToastService
  ) {}

  ngOnInit(): void {
    this.cargar();
    this.drawer.entitySaved$.pipe(takeUntil(this.destroy$)).subscribe(() => this.cargar());
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  cargar(): void {
    this.loading = true;
    this.error = '';
    this.configApi.getPriorities().pipe(takeUntil(this.destroy$)).subscribe({
      next: (data) => {
        this.items = data.sort((a, b) => a.level - b.level);
        this.aplicarFiltros();
        this.loading = false;
      },
      error: (err) => {
        this.error = err.error?.message || 'Error al cargar prioridades';
        this.loading = false;
      }
    });
  }

  aplicarFiltros(): void {
    const term = this.filtroNombre.trim().toLowerCase();
    this.filtered = this.items.filter((p) => {
      if (term && !`${p.label} ${p.name}`.toLowerCase().includes(term)) return false;
      if (this.filtroSoloActivas && !p.isActive) return false;
      return true;
    });
  }

  nuevo(): void {
    this.drawer.open();
  }

  editar(item: TicketPriority): void {
    this.drawer.open({ id: item.id });
  }

  toggleActiva(item: TicketPriority): void {
    // Toggle isActive con update — sin toast (cambio visual evidente).
    this.configApi
      .updatePriority(item.id, {
        name: item.name,
        label: item.label,
        color: item.color ?? undefined,
        level: item.level,
        isActive: !item.isActive
      })
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: () => this.cargar(),
        error: (err) => {
          this.toast.error({
            title: 'Error',
            message: err.error?.message || 'No se pudo actualizar la prioridad'
          });
        }
      });
  }

  async eliminar(item: TicketPriority): Promise<void> {
    const ok = await this.confirmDialog.confirmDanger({
      title: 'Eliminar prioridad',
      message: `¿Eliminar la prioridad "${item.label}"?`
    });
    if (!ok) return;
    this.configApi.deletePriority(item.id).pipe(takeUntil(this.destroy$)).subscribe({
      next: () => {
        this.toast.deleted('Prioridad', `Se eliminó "${item.label || item.name}" correctamente.`);
        this.cargar();
      },
      error: (err) => {
        this.toast.error({
          title: 'Error',
          message: err.error?.message || 'No se pudo eliminar la prioridad'
        });
      }
    });
  }

  volverATickets(): void {
    this.router.navigate(['/tickets']);
  }
}
